import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BriefcaseBusiness,
  MapPinned,
  Pencil,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import { campaignBelongsToWorkspace } from '../../lib/workspace';
import type {
  Campaign,
  ClientAgencyRelationship,
  Organization,
} from '../../types/domain';

interface CampaignDraft {
  owner_organization: string;
  client: string;
  operating_agency: string;
  name: string;
  description: string;
  starts_on: string;
  ends_on: string;
  status: Campaign['status'];
}

interface CampaignFilters {
  search: string;
  status: string;
  client: string;
  operator: string;
  dateFrom: string;
  dateTo: string;
}

type CampaignSortField = 'name' | 'client__name' | 'operator_name' | 'starts_on';
type CampaignOrdering = CampaignSortField | `-${CampaignSortField}`;

const emptyDraft: CampaignDraft = {
  owner_organization: '',
  client: '',
  operating_agency: '',
  name: '',
  description: '',
  starts_on: '',
  ends_on: '',
  status: 'draft',
};

const emptyFilters: CampaignFilters = {
  search: '',
  status: '',
  client: '',
  operator: '',
  dateFrom: '',
  dateTo: '',
};

function filtersFromSearchParams(searchParams: URLSearchParams): CampaignFilters {
  return {
    search: searchParams.get('search') ?? '',
    status: searchParams.get('status') ?? '',
    client: searchParams.get('client') ?? '',
    operator: searchParams.get('operator') ?? '',
    dateFrom: searchParams.get('date_from') ?? '',
    dateTo: searchParams.get('date_to') ?? '',
  };
}

const pageSize = 25;

const dateFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

export function CampaignsPage() {
  const {
    accessToken,
    capabilities,
    isPlatformAdmin,
    selectedWorkspace,
  } = useAuth();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const canManageCampaigns = capabilities.includes('campaigns:manage');
  const [editing, setEditing] = useState<Campaign | 'new' | null>(null);
  const [draft, setDraft] = useState<CampaignDraft>(emptyDraft);
  const [filters, setFilters] = useState<CampaignFilters>(
    () => filtersFromSearchParams(searchParams),
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    () => searchParams.get('search')?.trim() ?? '',
  );
  const [ordering, setOrdering] = useState<CampaignOrdering>('-starts_on');
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSearch(filters.search.trim()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [filters.search]);
  const campaignPath = (() => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      ordering,
    });
    if (selectedWorkspace?.organizationId) {
      params.set('workspace', selectedWorkspace.organizationId);
    }
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filters.status) params.set('status', filters.status);
    if (filters.client) params.set('client', filters.client);
    if (filters.operator) params.set('operator', filters.operator);
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    return `/campaigns/?${params.toString()}`;
  })();
  const campaigns = useApiCollection<Campaign>(
    'campaigns',
    campaignPath,
    accessToken,
  );
  const organizations = useApiCollection<Organization>(
    'campaign-organizations',
    '/organizations/?page_size=100&ordering=name',
    accessToken,
  );
  const relationships = useApiCollection<ClientAgencyRelationship>(
    'campaign-relationships',
    '/organization-relationships/?page_size=100&is_active=true',
    accessToken,
    canManageCampaigns,
  );
  const rows = campaigns.data?.results ?? [];
  const organizationRows = organizations.data?.results ?? [];
  const relationshipRows = relationships.data?.results ?? [];
  const clientOptions = organizationRows.filter((organization) => organization.type === 'client');
  const operatorOptions = organizationRows.filter((organization) => organization.type === 'agency');
  const totalCount = campaigns.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const firstVisiblePage = Math.max(1, Math.min(page - 2, totalPages - 4));
  const visiblePages = Array.from(
    { length: Math.min(5, totalPages) },
    (_, index) => firstVisiblePage + index,
  );
  const owners = organizationRows.filter((organization) => (
    (organization.type === 'agency' || organization.type === 'client')
    && (
      isPlatformAdmin
      || organization.id === selectedWorkspace?.organizationId
    )
  ));
  const selectedOwner = owners.find(
    (organization) => organization.id === draft.owner_organization,
  );
  const availableClients = selectedOwner?.type === 'client'
    ? organizationRows.filter((organization) => organization.id === selectedOwner.id)
    : organizationRows.filter((organization) => (
        organization.type === 'client'
        && relationshipRows.some((relationship) => (
          relationship.agency === selectedOwner?.id
          && relationship.client === organization.id
          && relationship.is_active
        ))
      ));
  const availableAgencies = organizationRows.filter((organization) => (
    organization.type === 'agency'
    && relationshipRows.some((relationship) => (
      relationship.agency === organization.id
      && relationship.client === draft.client
      && relationship.is_active
    ))
  ));

  const mutation = useMutation({
    mutationFn: () => {
      const creating = editing === 'new';
      const payload = creating
        ? {
            ...draft,
            operating_agency: draft.operating_agency || null,
          }
        : {
            name: draft.name,
            description: draft.description,
            starts_on: draft.starts_on,
            ends_on: draft.ends_on,
            status: draft.status,
            operating_agency: draft.operating_agency || null,
          };
      return apiRequest<Campaign>(
        creating ? '/campaigns/' : `/campaigns/${editing?.id}/`,
        {
          ...(accessToken ? { accessToken } : {}),
          method: creating ? 'POST' : 'PATCH',
          body: JSON.stringify(payload),
        },
      );
    },
    onSuccess: async () => {
      setEditing(null);
      setDraft(emptyDraft);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'campaigns'] });
    },
  });

  function ownerDefaults(ownerId: string): Pick<CampaignDraft, 'owner_organization' | 'client' | 'operating_agency'> {
    const owner = owners.find((organization) => organization.id === ownerId);
    return {
      owner_organization: ownerId,
      client: owner?.type === 'client' ? ownerId : '',
      operating_agency: owner?.type === 'agency' ? ownerId : '',
    };
  }

  function beginCreate() {
    mutation.reset();
    const workspaceOwner = selectedWorkspace?.organizationId
      && owners.some((owner) => owner.id === selectedWorkspace.organizationId)
      ? selectedWorkspace.organizationId
      : '';
    setDraft({ ...emptyDraft, ...ownerDefaults(workspaceOwner || '') });
    setEditing('new');
  }

  function beginEdit(campaign: Campaign) {
    mutation.reset();
    setDraft({
      owner_organization: campaign.owner_organization,
      client: campaign.client,
      operating_agency: campaign.operating_agency ?? '',
      name: campaign.name,
      description: campaign.description,
      starts_on: campaign.starts_on,
      ends_on: campaign.ends_on,
      status: campaign.status,
    });
    setEditing(campaign);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  function closeForm() {
    if (mutation.isPending) return;
    setEditing(null);
    setDraft(emptyDraft);
    mutation.reset();
  }

  function updateFilter<Key extends keyof CampaignFilters>(
    key: Key,
    value: CampaignFilters[Key],
  ) {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  }

  function resetFilters() {
    setFilters(emptyFilters);
    setDebouncedSearch('');
    setPage(1);
  }

  function toggleOrdering(field: CampaignSortField) {
    setOrdering((current) => (
      current === field ? `-${field}` : field
    ));
    setPage(1);
  }

  function sortDirection(field: CampaignSortField): 'ascending' | 'descending' | 'none' {
    if (ordering === field) return 'ascending';
    if (ordering === `-${field}`) return 'descending';
    return 'none';
  }

  function sortIcon(field: CampaignSortField) {
    const direction = sortDirection(field);
    if (direction === 'ascending') return <ArrowUp aria-hidden="true" size={14} />;
    if (direction === 'descending') return <ArrowDown aria-hidden="true" size={14} />;
    return <ArrowUpDown aria-hidden="true" size={14} />;
  }

  const canManageRow = (campaign: Campaign) => isPlatformAdmin || (
    canManageCampaigns && campaignBelongsToWorkspace(campaign, selectedWorkspace)
  );

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>Campaigns</h1>
          <p>Create client campaigns, select an authorized operator, and prepare field execution.</p>
        </div>
        {canManageCampaigns ? (
          <button className="button primary" onClick={beginCreate} type="button">
            <Plus size={17} /> Add campaign
          </button>
        ) : (
          <span className="record-count"><BriefcaseBusiness size={16} /> {totalCount}</span>
        )}
      </header>

      {editing ? (
        <div
          className="confirmation-backdrop"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeForm(); }}
          role="presentation"
        >
          <section
            aria-labelledby="campaign-form-dialog-title"
            aria-modal="true"
            className="confirmation-dialog campaign-form-dialog"
            role="dialog"
          >
            <button
              aria-label="Close campaign form"
              className="icon-button confirmation-close"
              disabled={mutation.isPending}
              onClick={closeForm}
              type="button"
            >
              <X size={19} />
            </button>
            <div className="card-heading campaign-form-dialog-heading">
            <div>
              <h2 id="campaign-form-dialog-title">{editing === 'new' ? 'Add campaign' : `Edit ${editing.name}`}</h2>
              <p>Agency and client choices are checked against active platform relationships.</p>
            </div>
            </div>
            <form className="management-form" onSubmit={submit}>
              <div className="form-grid">
                <label>
                  Owner organisation
                  <select
                    disabled={editing !== 'new'}
                    required
                    value={draft.owner_organization}
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      ...ownerDefaults(event.target.value),
                    }))}
                  >
                    <option value="">Select owner</option>
                    {owners.map((organization) => <option key={organization.id} value={organization.id}>{organization.name} · {organization.type}</option>)}
                  </select>
                </label>
                <label>
                  Client
                  <select
                    disabled={editing !== 'new' || !selectedOwner}
                    required
                    value={draft.client}
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      client: event.target.value,
                      operating_agency: selectedOwner?.type === 'agency'
                        ? selectedOwner.id
                        : '',
                    }))}
                  >
                    <option value="">Select client</option>
                    {availableClients.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
                  </select>
                </label>
                <label>
                  Operating agency
                  <select
                    disabled={editing !== 'new' && selectedOwner?.type === 'agency'}
                    value={draft.operating_agency}
                    onChange={(event) => setDraft({ ...draft, operating_agency: event.target.value })}
                  >
                    {selectedOwner?.type === 'client' ? <option value="">Direct client operation</option> : <option value="">Select agency</option>}
                    {availableAgencies.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
                  </select>
                </label>
                <label>
                  Campaign name
                  <input required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
                </label>
                <label>
                  Starts on
                  <input required type="date" value={draft.starts_on} onChange={(event) => setDraft({ ...draft, starts_on: event.target.value })} />
                </label>
                <label>
                  Ends on
                  <input required min={draft.starts_on} type="date" value={draft.ends_on} onChange={(event) => setDraft({ ...draft, ends_on: event.target.value })} />
                </label>
                <label>
                  Status
                  <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as Campaign['status'] })}>
                    <option value="draft">Draft</option>
                    <option value="staffing">Staffing</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
                <label className="wide-field">
                  Description
                  <textarea rows={3} value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
                </label>
              </div>
              {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
              <div className="form-actions">
                <button className="button secondary" disabled={mutation.isPending} onClick={closeForm} type="button">Cancel</button>
                <button className="button primary" disabled={mutation.isPending} type="submit">{mutation.isPending ? <InlineSpinner /> : null}{mutation.isPending ? 'Saving…' : 'Save campaign'}</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      <section className="content-card attendance-filter-card campaign-filter-card">
        <div className="card-heading">
          <div><h2>Find campaigns</h2><p>Search, narrow, and sort the campaigns available in this workspace.</p></div>
          <button className="text-button" onClick={resetFilters} type="button">Reset filters</button>
        </div>
        <div className="attendance-filter-grid campaign-filter-grid">
          <label className="attendance-search-filter">
            Search
            <span><Search size={16} /><input aria-label="Campaign, reference, client or operator" onChange={(event) => updateFilter('search', event.target.value)} placeholder="Search campaigns" value={filters.search} /></span>
          </label>
          <label>
            Status
            <select aria-label="Campaign status" onChange={(event) => updateFilter('status', event.target.value)} value={filters.status}>
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="staffing">Staffing</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          <label>
            Client
            <select aria-label="Campaign client" onChange={(event) => updateFilter('client', event.target.value)} value={filters.client}>
              <option value="">All clients</option>
              {clientOptions.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
            </select>
          </label>
          <label>
            Operator
            <select aria-label="Campaign operator" onChange={(event) => updateFilter('operator', event.target.value)} value={filters.operator}>
              <option value="">All operators</option>
              <option value="direct">Direct client</option>
              {operatorOptions.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
            </select>
          </label>
          <label>Date from<input max={filters.dateTo || undefined} onChange={(event) => updateFilter('dateFrom', event.target.value)} type="date" value={filters.dateFrom} /></label>
          <label>Date to<input min={filters.dateFrom || undefined} onChange={(event) => updateFilter('dateTo', event.target.value)} type="date" value={filters.dateTo} /></label>
        </div>
      </section>

      <DataPanel loading={campaigns.isLoading} error={campaigns.isError} empty={rows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap campaign-table-wrap">
            <table className="data-table campaign-table">
              <thead><tr>
                <th aria-sort={sortDirection('name')}><button aria-label="Sort by campaign" className="table-sort-button" onClick={() => toggleOrdering('name')} type="button">Campaign {sortIcon('name')}</button></th>
                <th aria-sort={sortDirection('client__name')}><button aria-label="Sort by client" className="table-sort-button" onClick={() => toggleOrdering('client__name')} type="button">Client {sortIcon('client__name')}</button></th>
                <th aria-sort={sortDirection('operator_name')}><button aria-label="Sort by operator" className="table-sort-button" onClick={() => toggleOrdering('operator_name')} type="button">Operator {sortIcon('operator_name')}</button></th>
                <th aria-sort={sortDirection('starts_on')}><button aria-label="Sort by dates" className="table-sort-button" onClick={() => toggleOrdering('starts_on')} type="button">Dates {sortIcon('starts_on')}</button></th>
                <th>Status</th><th><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>
                {rows.map((campaign) => (
                  <tr key={campaign.id}>
                    <td><strong>{campaign.name}</strong><small>{campaign.reference} · {campaign.owner_name}</small></td>
                    <td>{campaign.client_name}</td>
                    <td>{campaign.operating_agency_name ?? 'Direct client'}</td>
                    <td>{dateFormatter.format(new Date(campaign.starts_on))} – {dateFormatter.format(new Date(campaign.ends_on))}</td>
                    <td><StatusPill value={campaign.status} /></td>
                    <td>
                      {canManageRow(campaign) ? (
                        <div className="table-actions">
                          <button className="text-button" onClick={() => beginEdit(campaign)} type="button"><Pencil size={14} /> Edit</button>
                          <Link className="text-button" to={`/campaigns/${campaign.id}/setup`}><MapPinned size={14} /> Campaign setup</Link>
                        </div>
                      ) : <Link className="text-button" to={`/campaigns/${campaign.id}/setup`}><MapPinned size={14} /> View campaign</Link>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="attendance-pagination campaign-pagination">
            <span>Page {page} of {totalPages} · {totalCount} matching campaigns</span>
            <div>
              <button className="button secondary" disabled={!campaigns.data?.previous} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button>
              {visiblePages.map((pageNumber) => (
                <button
                  aria-current={pageNumber === page ? 'page' : undefined}
                  className={`button secondary page-number${pageNumber === page ? ' active' : ''}`}
                  key={pageNumber}
                  onClick={() => setPage(pageNumber)}
                  type="button"
                >
                  {pageNumber}
                </button>
              ))}
              <button className="button secondary" disabled={!campaigns.data?.next} onClick={() => setPage((current) => Math.min(totalPages, current + 1))} type="button">Next</button>
            </div>
          </div>
        </section>
      </DataPanel>
    </>
  );
}
